<template>
    <div class="EvieChatLanding">
        <div class="EvieChatLanding__hero">
            <div class="EvieChatLanding__iconWrapper">
                <EidIcon class="EvieChatLanding__icon" :icon-src="faSparkles" />
            </div>
            <h1 class="EvieChatLanding__heading">
                Meet Evie AI
            </h1>
            <p class="EvieChatLanding__subtitle">
                Your intelligent AI assistant for getting work done.
            </p>
            <p class="EvieChatLanding__description">
                Evie helps you evaluate information, upload and review documents, surface insights,
                and streamline complex processes — all with a simple question. Less manual effort,
                faster answers, and clearer next steps, built directly into your workflow.
            </p>
            <NewButton
                class="EvieChatLanding__cta"
                variant="gradient"
                color="primary"
                size="default"
                type="button"
                @click="$emit('new-chat')"
            >
                <EidIcon :icon-src="faPlus" />
                New Chat
            </NewButton>
        </div>
        <div class="EvieChatLanding__prompts">
            <EvieChatLandingPrompt
                v-for="item in starterPrompts"
                :key="item.text"
                :prompt="item.text"
                :icon="item.icon"
                :icon-color="item.iconColor"
                :icon-bg="item.iconBg"
                @click="$emit('send', item.text)"
            />
        </div>
    </div>
</template>

<style lang="scss">
.EvieChatLanding {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 24px;
    width: 100%;
    max-width: 720px;
    margin: 0 auto;
    height: 100%;
    padding: 40px 16px;
    overflow-y: auto;

    &__hero {
        display: flex;
        flex-direction: column;
        align-items: center;
        gap: 12px;
        text-align: center;
        width: 100%;
    }

    &__iconWrapper {
        display: flex;
        align-items: center;
        justify-content: center;
        width: 64px;
        height: 64px;
        border-radius: 16px;
        background: linear-gradient(var(--eidSafetyBlue), var(--eidTrustGreen));
        color: var(--eidWhite);
        font-size: 28px;
        margin-bottom: 4px;
    }

    &__icon {
        font-size: 28px;
    }

    &__heading {
        font-family: var(--evie-font-stack);
        font-size: 28px;
        font-weight: 700;
        margin: 0;
        color: var(--eidQualityBlack);
    }

    &__subtitle {
        font-size: 15px;
        color: var(--eidMediumGray);
        margin: 0;
    }

    &__description {
        font-size: 14px;
        color: var(--eidMediumGray);
        line-height: 1.6;
        margin: 0;
    }

    &__cta {
        margin-top: 8px;
        gap: 6px;
    }

    &__prompts {
        display: flex;
        flex-direction: column;
        gap: 8px;
        width: 100%;
    }
}
</style>

<script lang="ts">
    import { defineComponent } from 'vue';

    import { faPlus } from '@fortawesome/pro-light-svg-icons';
    import { faSparkles } from '@fortawesome/pro-regular-svg-icons';

    import EidIcon from '@evidentid/dashboard-commons/components/EidIcon/EidIcon.vue';

    import NewButton from '@/components/new-button/NewButton.vue';
    import { STARTER_PROMPTS } from '@/modules/evie-ai/utils/starterPrompts';

    import EvieChatLandingPrompt from './sub-components/EvieChatLandingPrompt.vue';

    export default defineComponent({
        name: 'EvieChatLanding',
        components: { EidIcon, NewButton, EvieChatLandingPrompt },
        emits: [ 'new-chat', 'send' ],
        data() {
            return {
                faSparkles,
                faPlus,
                starterPrompts: STARTER_PROMPTS,
            };
        },
    });
</script>
